import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { FileSpreadsheet } from 'lucide-react'
import { Button, EmptyState, PageHeaderBand, QueryErrorState, Select } from '@/components/ui'
import { PartnerPicker } from '@/components/ui/PartnerPicker/PartnerPicker'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { useAuth, useHasAnyPermission } from '@/shell/module-system/permissions'
import { useForecastLines, useMyCustomers } from '../api/queries'
import { ForecastImportDrawer } from '../components/ForecastImportDrawer'
import { ForecastLinesGrid } from '../components/ForecastLinesGrid'

const ROLLUP_PERMISSIONS = ['admin', 'operations', 'operations_logistics', 'purchasing', 'sales'] as const

export function ForecastEntryPage() {
  const { scoped } = useAuth()
  const canSeeRollup = useHasAnyPermission(ROLLUP_PERMISSIONS)

  // Scoped reps (outside_sales_rep / inside_sales_rep) pick from their
  // assigned customers only; everyone else searches the full customer
  // list. Mirrors the Sprint 16 `scoped` flag already used for the
  // "Showing your customers (N)" chip elsewhere in the shell.
  const { data: myCustomers } = useMyCustomers(!!scoped)
  const [scopedCustomerId, setScopedCustomerId] = useState<number | null>(null)
  const [searchedCustomer, setSearchedCustomer] = useState<PickerValue>(null)
  const [importOpen, setImportOpen] = useState(false)

  const effectiveCustomerId = scoped
    ? scopedCustomerId ?? myCustomers?.[0]?.id ?? null
    : searchedCustomer?.[0] ?? null

  const { data: lines, isLoading, isError, error, refetch } = useForecastLines(effectiveCustomerId)

  return (
    <div data-density="comfortable" className="flex h-full flex-col">
      <PageHeaderBand
        title="Demand Forecast"
        description="Enter forecasted product quantities per customer, over whatever period the customer gives you — monthly, a year-end lump sum, first six months, or anything in between."
      >
        {canSeeRollup ? (
          <Link
            to="/demand-forecast/rollup"
            className="ml-auto text-sm text-link hover:underline"
          >
            Procurement rollup →
          </Link>
        ) : null}
      </PageHeaderBand>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
        <div className="flex items-start gap-3">
        <div className="w-full max-w-sm">
          {scoped ? (
            <Select
              aria-label="Customer"
              value={effectiveCustomerId ?? ''}
              onChange={(e) => setScopedCustomerId(Number(e.target.value) || null)}
            >
              {!myCustomers?.length ? <option value="">No customers assigned</option> : null}
              {myCustomers?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          ) : (
            <PartnerPicker
              kind="customer"
              value={searchedCustomer}
              onChange={setSearchedCustomer}
              placeholder="Search customers…"
              variant="field"
            />
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="ml-auto"
          disabled={effectiveCustomerId === null}
          onClick={() => setImportOpen(true)}
        >
          <FileSpreadsheet size={14} />
          Import spreadsheet
        </Button>
        </div>

        <div className="min-h-0 flex-1">
          {effectiveCustomerId === null ? (
            <EmptyState
              variant="no-data"
              title="Pick a customer"
              description="Select a customer above to view or enter their demand forecast."
            />
          ) : isError ? (
            <QueryErrorState
              error={error}
              onRetry={() => void refetch()}
              subject="forecast lines"
            />
          ) : (
            <ForecastLinesGrid
              key={effectiveCustomerId}
              customerId={effectiveCustomerId}
              lines={isLoading ? [] : (lines ?? [])}
              linesLoading={isLoading}
            />
          )}
        </div>
      </div>
      {effectiveCustomerId !== null ? (
        <ForecastImportDrawer
          open={importOpen}
          onOpenChange={setImportOpen}
          customerId={effectiveCustomerId}
        />
      ) : null}
    </div>
  )
}
